import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collaboratorPortalAuth, getCompanyInfo } from '../../../../services/PublicBookingService';
import usePortalInstall from './usePortalInstall';
import '../booking/PublicBooking.css';
import './CollaboratorPortalLogin.css';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import { ThemeContext } from '../../../../context/ThemeContext';
import packageInfo from '../../../../../package.json';

const resolvePublicAssetUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const CollaboratorPortalLoginPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { appTheme, setAppTheme, toggleAppTheme } = useContext(ThemeContext);

    const [company, setCompany] = useState(null);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [companyLoading, setCompanyLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForgotHint, setShowForgotHint] = useState(false);

    // Identidade do portal para "adicionar à tela inicial" (nome fixo, ícone da empresa).
    usePortalInstall(company);

    useEffect(() => {
        // O portal do colaborador usa o tema escuro como padrão, sem herdar a preferência do painel.
        setAppTheme('dark');
    }, [setAppTheme]);

    useEffect(() => {
        getCompanyInfo(slug)
            .then(res => setCompany(res.data))
            .catch(() => {})
            .finally(() => setCompanyLoading(false));
    }, [slug]);

    useEffect(() => {
        // O PWA pode ter sido instalado a partir da tela de login, que fica como start_url.
        // Reaproveita a sessão persistida ao reabrir o app para não pedir login toda vez.
        const storageKey = `colmeia_portal_token_${slug}`;
        try {
            const stored = localStorage.getItem(storageKey);
            if (!stored) return;

            const { token, expiresAt } = JSON.parse(stored);
            if (token && (!expiresAt || new Date(expiresAt) >= new Date())) {
                navigate(`/p/${slug}/portal`, { replace: true });
                return;
            }

            localStorage.removeItem(storageKey);
        } catch {
            localStorage.removeItem(storageKey);
        }
    }, [slug, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            // O colaborador é identificado pelo e-mail; não há employeeUuid na URL.
            const res = await collaboratorPortalAuth({ companySlug: slug, username, password });
            const { token, expiresAt } = res.data;
            // Token guardado no localStorage por slug da empresa — nunca exposto na URL.
            const storageKey = `colmeia_portal_token_${slug}`;
            localStorage.setItem(storageKey, JSON.stringify({ token, expiresAt }));
            navigate(`/p/${slug}/portal`, { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data;
            if (typeof msg === 'string' && msg.length < 200) {
                setError(msg);
            } else {
                setError('E-mail ou senha inválidos.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (companyLoading) {
        return (
            <div className="public-booking-page collab-login-page">
                <div className="collab-login-center">
                    <div className="collab-login-spinner"></div>
                </div>
            </div>
        );
    }

    const logoUrl = resolvePublicAssetUrl(company?.logoUrl);

    return (
        <div className="public-booking-page collab-login-page">
            <div className="collab-login-center">
                <div className="collab-login-card">
                    <button
                        type="button"
                        className="portal-theme-toggle"
                        onClick={toggleAppTheme}
                        aria-label={appTheme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
                        title={appTheme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
                    >
                        <i className={`fa-solid ${appTheme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
                    </button>
                    {/* Company branding */}
                    <div className="collab-login-brand">
                        {logoUrl ? (
                            <img src={logoUrl} alt={company?.name} className="collab-login-logo" />
                        ) : (
                            <div className="collab-login-logo-placeholder">
                                {company?.name?.charAt(0) || '?'}
                            </div>
                        )}
                        <span className="collab-login-company-name">{company?.name || 'Portal do Colaborador'}</span>
                    </div>

                    <h1 className="collab-login-title">Entrar no portal</h1>
                    <p className="collab-login-subtitle">Acesse para ver sua agenda e comissões.</p>

                    <form onSubmit={handleSubmit} className="collab-login-form" noValidate>
                        <div className="collab-login-field">
                            <label htmlFor="collab-username">E-mail</label>
                            <input
                                id="collab-username"
                                type="email"
                                autoComplete="email"
                                placeholder="seu@email.com"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="collab-login-field">
                            <label htmlFor="collab-password">Senha</label>
                            <div className="collab-login-password-wrap">
                                <input
                                    id="collab-password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="collab-login-eye"
                                    onClick={() => setShowPassword(v => !v)}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>

                        <div className="collab-login-forgot">
                            <button
                                type="button"
                                className="collab-login-forgot-link"
                                onClick={() => setShowForgotHint(v => !v)}
                            >
                                Esqueceu a senha?
                            </button>
                        </div>

                        {showForgotHint && (
                            <div className="collab-login-hint" role="note">
                                <i className="fa-solid fa-circle-info"></i>
                                <span>
                                    Sua senha de acesso é definida por {company?.name || 'sua empresa'}.
                                    Peça ao responsável para redefinir no seu cadastro de colaborador.
                                </span>
                            </div>
                        )}

                        {error && (
                            <div className="collab-login-error" role="alert">
                                <i className="fa-solid fa-circle-exclamation"></i>
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="collab-login-submit"
                            disabled={loading || !username || !password}
                        >
                            {loading
                                ? <><span className="collab-login-btn-spinner"></span>Entrando…</>
                                : 'Entrar'}
                        </button>
                    </form>

                    <PublicBrandSignature className="is-compact" />
                    <div className="collab-login-version">v{packageInfo.version}</div>
                </div>
            </div>
        </div>
    );
};

export default CollaboratorPortalLoginPage;
